import {
  BookkeepingProgressionInfo as BookkeepingProgressionInfoProto,
  CompleteBookkeepingCycleRequest,
  ConfirmUnmanagedClientAccessRequest,
  ConfirmUnmanagedPlatformAccessRequest,
  CorrectBookkeepingCycleRequest,
  GetBookkeepingProgressionInfoRequest,
  StartNewBookkeepingCycleRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { collection } from 'infra/collection'
import { BookkeepingAPI } from 'jobs/API'
import BookkeepingProgressionInfo from './BookkeepingProgressionInfo'
import { Id } from 'types/definitions'
import { map } from 'rxjs'
import { filterNonNil } from 'utils/rx-util'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { BookkeepingEvent } from './BookkeepingEvent'
import {
  BookkeepingSoftware,
  mapBookkeepingSoftwareToProto,
} from './BookkeepingSoftware'

interface CompletedItemPayload {
  id: Id
  completedCount: number
}

interface CompleteBookkeepingCyclePayload {
  jobId: Id
  cycleId: Id
  completedItems: CompletedItemPayload[]
}

interface CorrectedItemPayload {
  id: Id
  completedCount: number
}

interface CorrectBookkeepingCyclePayload {
  jobId: Id
  cycleId: Id
  correctedItems: CorrectedItemPayload[]
}

export default class BookkeepingProgressionStore {
  bookkeepingProgressions = collection<
    BookkeepingProgressionInfoProto.AsObject,
    BookkeepingProgressionInfo
  >({
    create: () => new BookkeepingProgressionInfo(),
    getModelId: (bpi) => bpi.id,
    getDataId: (attrs) => attrs.id,
  })

  /**
   * Bookkeeping events from the realtime channel.
   */
  readonly events$ = this.api.events$.pipe(
    map((e) => this.mapBookkeepingRealtimeEvent(e)),
    filterNonNil(),
  )

  constructor(private api: BookkeepingAPI) {}

  /**
   * Fetches the bookkeeping info for the given job and adds it to the collection if exists.
   * @param jobId
   */
  async fetchBookkeepingProgressionInfo(jobId: Id) {
    const request = new GetBookkeepingProgressionInfoRequest().setId(
      Id.assert(jobId),
    )

    const response = await this.api.getBookkeepingProgressionInfo(request)

    if (response.hasBookkeepingProgressionInfo()) {
      this.bookkeepingProgressions.set(
        response.getBookkeepingProgressionInfo()?.toObject(),
      )
    }
  }

  /**
   * Returns the bookkeeping progression info for the given job id.
   * @param jobId
   * @returns
   */
  getBookkeepingProgressionInfo(jobId: Id) {
    return this.bookkeepingProgressions.get(jobId)
  }

  /**
   * Confirms that the Pro has access to the bookkeeping platform.
   * @param jobId
   */
  async confirmUnmanagedPlatformAccess(jobId: Id) {
    const request = new ConfirmUnmanagedPlatformAccessRequest().setId(
      Id.assert(jobId),
    )

    await this.api.confirmUnmanagedPlatformAccess(request)

    await this.fetchBookkeepingProgressionInfo(jobId)
  }

  /**
   * Confirms that the client granted the Pro access to their books.
   * @param jobId
   * @param software The software the client uses for their books.
   */
  async confirmUnmanagedClientAccess(jobId: Id, software: BookkeepingSoftware) {
    const request = new ConfirmUnmanagedClientAccessRequest()
      .setId(Id.assert(jobId))
      .setSoftware(mapBookkeepingSoftwareToProto(software))

    await this.api.confirmUnmanagedClientAccess(request)

    await this.fetchBookkeepingProgressionInfo(jobId)
  }

  /**
   * Starts a new bookkeeping cycle.
   * @param jobId
   */
  async startNewBookkeepingCycle(jobId: Id) {
    const request = new StartNewBookkeepingCycleRequest().setId(
      Id.assert(jobId),
    )

    await this.api.startNewBookkeepingCycle(request)

    await this.fetchBookkeepingProgressionInfo(jobId)
  }

  /**
   * Completes a bookkeeping cycle.
   * @param payload
   */
  async completeBookkeepingCycle(payload: CompleteBookkeepingCyclePayload) {
    const request = new CompleteBookkeepingCycleRequest()
      .setId(Id.assert(payload.jobId))
      .setCycleId(Id.assert(payload.cycleId))
      .setCompletedItemsList(
        payload.completedItems.map((ci) =>
          new CompleteBookkeepingCycleRequest.CompletedItem()
            .setId(Id.assert(ci.id))
            .setCompletedCount(ci.completedCount),
        ),
      )

    await this.api.completeBookkeepingCycle(request)

    await this.fetchBookkeepingProgressionInfo(payload.jobId)
  }

  /**
   * Corrects a bookkeeping cycle.
   * @param payload
   */
  async correctBookkeepingCycle(payload: CorrectBookkeepingCyclePayload) {
    const request = new CorrectBookkeepingCycleRequest()
      .setId(Id.assert(payload.jobId))
      .setCycleId(Id.assert(payload.cycleId))
      .setCorrectedItemsList(
        payload.correctedItems.map((ci) =>
          new CorrectBookkeepingCycleRequest.CorrectedItem()
            .setId(Id.assert(ci.id))
            .setCompletedCount(ci.completedCount),
        ),
      )

    await this.api.correctBookkeepingCycle(request)

    await this.fetchBookkeepingProgressionInfo(payload.jobId)
  }

  /**
   * Maps an event from the realtime channel and updates the local representation
   * of the associated bookkeeping progression.
   *
   * @param event
   * @private
   */
  private mapBookkeepingRealtimeEvent(
    event: RealtimeEvent,
  ): BookkeepingEvent | null {
    const dto = event.getBookkeepingUpdated()?.getProgressionInfo()?.toObject()
    if (dto) {
      return {
        type: 'BookkeepingUpdated',
        bookkeepingProgressionInfo: this.bookkeepingProgressions.set(dto),
      }
    }
    return null
  }
}
